import { httpClient } from './core';

// Communication Settings (SMS Provider + CTI)
export type SmsProvider = 'none' | 'sms77';
export type CtiProvider = 'none' | 'nfon';

export interface CommunicationSettingsDto {
  sms: { provider: SmsProvider; apiKey: string };
  cti: { provider: CtiProvider };
  maintenance?: {
    enabled: boolean;
    emailRedirectAddress: string;
    smsRedirectTo: string;
    // E-Mail-Typ-IDs, die den Wartungsmodus-Redirect umgehen (an echte Empfänger gehen).
    emailExemptKinds?: string[];
  };
}

export interface EmailKindItem {
  id: string;
  label: string;
}

export interface StandortCommunicationDto {
  smsSender: string;
  nfonAkz: string;
  emailFrom?: string;
  emailFromName?: string;
}

export const communicationSettingsApi = {
  get: async (): Promise<CommunicationSettingsDto> => {
    const res = await httpClient<CommunicationSettingsDto>('/api/settings/communication');
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Laden der Kommunikations-Einstellungen');
    return res.data;
  },
  update: async (payload: CommunicationSettingsDto): Promise<CommunicationSettingsDto> => {
    const res = await httpClient<CommunicationSettingsDto>('/api/settings/communication', {
      method: 'PUT',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Speichern der Kommunikations-Einstellungen');
    return res.data;
  },
  getEmailKinds: async (): Promise<EmailKindItem[]> => {
    const res = await httpClient<{ items: EmailKindItem[] }>('/api/settings/email-kinds');
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Laden der E-Mail-Typen');
    return res.data.items || [];
  },
  getStandorteMap: async (): Promise<Record<string, StandortCommunicationDto>> => {
    const res = await httpClient<Record<string, StandortCommunicationDto>>('/api/settings/communication/standorte');
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Laden der Standort-Kommunikation');
    return res.data;
  },
  updateStandort: async (standortId: number, payload: StandortCommunicationDto): Promise<StandortCommunicationDto> => {
    const res = await httpClient<StandortCommunicationDto>(`/api/settings/communication/standorte/${encodeURIComponent(String(standortId))}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Speichern des Standorts');
    return res.data;
  },
  testSms: async (payload: { standortId: number; to: string; text: string }): Promise<{ ok: boolean; error?: string; messageId?: string | null }> => {
    const res = await httpClient<{ ok: boolean; error?: string; messageId?: string | null }>('/api/settings/communication/sms/test', {
      method: 'POST',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Test-SMS fehlgeschlagen');
    return res.data;
  },
};

// Communications (universal message wizard): Preview + Send (HTML email + SMS)
export type CommunicationsChannel = 'email' | 'sms';
export type CommunicationsRecipient =
  | { type: 'person'; wpUserId: number; subIndex?: number | null; clientId?: string }
  | { type: 'trial_request'; trialRequestId: number; clientId?: string };

export type CommunicationsPreviewPayload = {
  channel: CommunicationsChannel;
  standortId: number;
  purpose?: 'transactional' | 'marketing';
  recipient: CommunicationsRecipient;
  templateKey?: string;
  draft?: Record<string, unknown>;
  headerLayoutId?: number | null;
  footerLayoutId?: number | null;
  vars?: Record<string, unknown>;
};

export type CommunicationsPreviewResponse =
  | { ok: true; subject: string; html: string; text?: string | null; used_layouts?: { header_layout_id: number | null; footer_layout_id: number | null } }
  | { ok: true; text: string };

export type CommunicationsSendPayload = {
  channel: CommunicationsChannel;
  standortId: number;
  purpose?: 'transactional' | 'marketing';
  recipients: CommunicationsRecipient[];
  templateKey?: string;
  draft?: Record<string, unknown>;
  headerLayoutId?: number | null;
  footerLayoutId?: number | null;
  vars?: Record<string, unknown>;
  /**
   * Aus welchem Vorgang heraus geschrieben wird. Ohne diesen Bezug landet der Versand
   * nur in der Akte der Person — in der Probestunde und in der Anfrage fehlte er.
   */
  context?: { entityType: 'trial' | 'trial_request' | 'person' | 'enrollment'; entityId: number };
};

export type CommunicationsSendResultRow = {
  index: number;
  recipient: Record<string, unknown>;
  ok: boolean;
  channel: CommunicationsChannel;
  to?: string;
  subject?: string;
  provider?: string | null;
  messageId?: string | null;
  error?: string;
};

export type CommunicationsSendResponse = { ok: boolean; results: CommunicationsSendResultRow[] };

export const communicationsApi = {
  resolveRecipients: async (payload: { standortId: number; recipients: CommunicationsRecipient[]; purpose?: 'transactional' | 'marketing' }): Promise<{ ok: boolean; items: Array<Record<string, unknown>> }> => {
    const res = await httpClient<{ ok: boolean; items: Array<Record<string, unknown>> }>('/api/communications/resolve-recipients', {
      method: 'POST',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Resolve fehlgeschlagen');
    return res.data;
  },
  preview: async (payload: CommunicationsPreviewPayload): Promise<Record<string, unknown>> => {
    const res = await httpClient<Record<string, unknown>>('/api/communications/preview', {
      method: 'POST',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Vorschau fehlgeschlagen');
    return res.data;
  },
  send: async (payload: CommunicationsSendPayload): Promise<CommunicationsSendResponse> => {
    const res = await httpClient<CommunicationsSendResponse>('/api/communications/send', {
      method: 'POST',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Versand fehlgeschlagen');
    return res.data;
  },
};

// SMS send (used by office/admin UI)
export const smsApi = {
  send: async (payload: { to: string; text: string; standortId: number }): Promise<{ ok: boolean; provider?: string; messageId?: string | null; error?: string }> => {
    const res = await httpClient<{ ok: boolean; provider?: string; messageId?: string | null; error?: string }>('/api/sms/send', {
      method: 'POST',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'SMS Versand fehlgeschlagen');
    return res.data;
  },
};

// E-Mail send (used by office/admin UI; SMTP via backend EmailService)
export const emailApi = {
  send: async (payload: { to: string; subject: string; text: string; standortId: number }): Promise<{ ok: boolean; error?: string; fromAddressUsed?: string | null; fromNameUsed?: string | null }> => {
    const res = await httpClient<{ ok: boolean; error?: string; fromAddressUsed?: string | null; fromNameUsed?: string | null }>('/api/email/send', {
      method: 'POST',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'E-Mail Versand fehlgeschlagen');
    return res.data;
  },
};
